import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getWholesaleNotifications, markNotificationRead, markAllNotificationsRead } from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

export function useWholesaleAuth(requiredRole) {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) { navigate('/login'); return; }
    axios.post(`${API}/api/auth/verify?token=${token}`)
      .then(res => {
        const u = res.data.user;
        if (requiredRole === 'wholesale' && !u.is_wholesale && u.role !== 'admin') { navigate('/login'); return; }
        if (requiredRole === 'admin' && u.role !== 'admin') { navigate('/login'); return; }
        setUser(u);
      })
      .catch(() => navigate('/login'));
  }, [navigate, requiredRole]);
  return user;
}

const retailerLinks = [
  { path: '/wholesale', label: 'Catalogue' },
  { path: '/wholesale/orders', label: 'My Orders' },
  { path: '/wholesale/profile', label: 'Profile' },
  { path: '/wholesale/conversations', label: 'Conversations' },
];
const adminLinks = [
  { path: '/wholesale/admin', label: 'Dashboard' },
  { path: '/wholesale/admin/orders', label: 'Orders' },
  { path: '/wholesale/admin/products', label: 'Products' },
  { path: '/wholesale/admin/users', label: 'Wholesalers' },
  { path: '/wholesale/admin/settings', label: 'Settings' },
  { path: '/wholesale/admin/conversations', label: 'Conversations' },
];

export const STATUS_COLORS = {
  pending:  { bg: '#FFF3E0', color: '#E65100' },
  approved: { bg: '#E8F5E9', color: '#2E7D32' },
  paid:     { bg: '#E3F2FD', color: '#1565C0' },
  shipped:  { bg: '#111', color: '#fff' },
  rejected: { bg: '#FCE4EC', color: '#B71C1C' },
};

export function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{ display:'inline-block', padding:'3px 10px', fontSize:10, letterSpacing:1, fontWeight:600, borderRadius:2, textTransform:'uppercase', background:s.bg, color:s.color }}>
      {status}
    </span>
  );
}

function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = notifications.filter(n => !n.read).length;

  const load = useCallback(() => {
    getWholesaleNotifications().then(r => setNotifications(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  const handleRead = async (nid) => {
    await markNotificationRead(nid).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === nid ? { ...n, read: true } : n));
  };

  const handleReadAll = async () => {
    await markAllNotificationsRead().catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} data-testid="ws-notification-bell" style={{ background:'none', border:'none', cursor:'pointer', position:'relative', padding:4 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        {unread > 0 && <span style={{ position:'absolute', top:0, right:0, width:16, height:16, borderRadius:'50%', background:'#C41E2A', color:'#fff', fontSize:9, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>{unread}</span>}
      </button>
      {open && <>
        <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:190 }} />
        <div style={{ position:'absolute', right:0, top:36, width:340, maxHeight:420, background:'#fff', border:'1px solid #E0DED8', borderRadius:4, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', zIndex:200, overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #E0DED8', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:12, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase' }}>Notifications</span>
            {unread > 0 && <button onClick={handleReadAll} style={{ fontSize:10, color:'#C41E2A', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Mark all read</button>}
          </div>
          <div style={{ flex:1, overflowY:'auto', maxHeight:360 }}>
            {notifications.length === 0 && <div style={{ padding:24, textAlign:'center', color:'#888', fontSize:12 }}>No notifications</div>}
            {notifications.map(n => (
              <div key={n.id} onClick={() => { if (!n.read) handleRead(n.id); }} style={{ padding:'12px 16px', borderBottom:'1px solid #F0EEE8', background: n.read ? '#fff' : '#FFFCF5', cursor:'pointer' }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#111' }}>{n.title}</div>
                <div style={{ fontSize:11, color:'#666', marginTop:2 }}>{n.body}</div>
                <div style={{ fontSize:10, color:'#aaa', marginTop:4 }}>{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </>}
    </div>
  );
}

export default function WholesaleLayout({ user, title, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const links = isAdmin ? adminLinks : retailerLinks;
  const roleBadge = isAdmin ? 'ADMIN' : 'RETAILER';

  const handleLogout = () => {
    const token = localStorage.getItem('auth_token');
    if (token) axios.post(`${API}/api/auth/logout?token=${token}`).catch(() => {});
    localStorage.removeItem('auth_token');
    window.location.href = '/login';
  };

  if (!user) return null;

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&family=Barlow+Condensed:wght@600;700;900&display=swap');*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Barlow',sans-serif}`}</style>
      <div style={{ display:'flex', minHeight:'100vh' }}>
        <aside style={{ position:'fixed', top:0, left:0, bottom:0, width:220, background:'#111', display:'flex', flexDirection:'column', zIndex:100 }}>
          <div style={{ padding:'28px 20px 20px' }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:26, fontWeight:700, color:'#fff', letterSpacing:2 }}>EXTREME</div>
            <div style={{ fontSize:10, color:'#888', letterSpacing:3, marginTop:2 }}>Wholesale Portal</div>
            <div style={{ display:'inline-block', marginTop:10, padding:'3px 10px', border:'1px solid #C41E2A', color:'#C41E2A', fontSize:9, letterSpacing:2, textTransform:'uppercase', borderRadius:2 }}>{roleBadge}</div>
          </div>
          <nav style={{ flex:1, padding:'8px 0' }}>
            {links.map(lk => {
              const active = location.pathname === lk.path;
              return (
                <a key={lk.path} href={lk.path} data-testid={`ws-nav-${lk.label.toLowerCase().replace(/\s/g,'-')}`}
                  onClick={e => { e.preventDefault(); navigate(lk.path); }}
                  style={{ display:'block', padding:'11px 20px', color: active?'#fff':'#aaa', fontSize:13, fontWeight:500, textDecoration:'none', borderLeft:`3px solid ${active?'#C41E2A':'transparent'}`, background: active?'rgba(255,255,255,0.05)':'transparent', transition:'all 0.15s' }}
                  onMouseEnter={e=>{if(!active){e.currentTarget.style.color='#fff';e.currentTarget.style.background='rgba(255,255,255,0.05)'}}}
                  onMouseLeave={e=>{if(!active){e.currentTarget.style.color='#aaa';e.currentTarget.style.background='transparent'}}}
                >{lk.label}</a>
              );
            })}
          </nav>
          <div style={{ padding:'16px 20px', borderTop:'1px solid #222' }}>
            <a href="#" onClick={e=>{e.preventDefault();handleLogout()}} style={{ color:'#666', fontSize:11, textDecoration:'none' }} data-testid="ws-logout">Sign Out</a>
          </div>
        </aside>
        <div style={{ marginLeft:220, flex:1, background:'#F7F6F2', minHeight:'100vh' }}>
          <div style={{ background:'#fff', borderBottom:'1px solid #E0DED8', height:56, padding:'0 32px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:50 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, fontWeight:700, letterSpacing:1, textTransform:'uppercase' }}>{title}</div>
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              <NotificationBell />
              <div style={{ fontSize:12, color:'#888' }}>{user?.display_name || user?.username}</div>
            </div>
          </div>
          <div style={{ padding:32 }}>{children}</div>
        </div>
      </div>
    </>
  );
}
